/* eslint-disable react/no-unstable-nested-components */
import { useMemo, useState } from "react";
import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
} from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { useTriggerSSOLaunch } from "../../hooks/e-prescribe.mutations";
import { useAppSelector } from "../../../../hooks/useRedux";
import useToast from "../../../../hooks/useToast";
import { ISSOLaunchResponse } from "../../types/e-prescribe.types";
import DrFirstIframe from "../DrFirstIframe/DrFirstIframe";
import { useGetClientMedications } from "../../hooks/e-prescribe.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import ClientMedicationListTable from "./ClientMedicationListTable";
import {
    displayNameInRightFormat,
    formatDate,
    getUniqueValuesFromList,
    truncateString,
} from "../../../../utils/helpers";
import { getFilterButtonText } from "../../helpers/e-prescribe.helpers";

interface ClientMedicationListProps {
    sessionHistoryId?: string;
}

export default function ClientMedicationList({
    sessionHistoryId,
}: ClientMedicationListProps) {
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );
    const { practice } = useAppSelector((state) => state.userPractice);
    const params = useParams();
    const { clientId } = params;
    const [searchParams, setSearchParams] = useSearchParams();

    const nameFilter = searchParams.get("name") ?? "";
    const frequencyFilter = searchParams.get("frequency") ?? "";
    const prescriberFilter = searchParams.get("prescriber") ?? "";
    const statusFilter = searchParams.get("status") ?? "";
    const startDateFilter = searchParams.get("startDate") ?? "";
    const endDateFilter = searchParams.get("endDate") ?? "";
    const createdDateFilter = searchParams.get("createdDate") ?? "";

    const triggerSSOLaunch = useTriggerSSOLaunch();

    const { data, isLoading, isSuccess, error } = useGetClientMedications({
        practiceId: practice?.practice_id ?? "",
        clientId: clientId ?? "",
        filters: { session_history_id: sessionHistoryId },
    });

    const actionButtonText = () => {
        if (isLoading) return "Loading...";
        if (isSuccess && data.data.length === 0) return "Add Medication";
        if (isSuccess && data.data.length === 1) return "Update Medication";
        return "Update Medications";
    };

    const { toast } = useToast();

    const medicationNamesInList = useMemo(
        () =>
            getUniqueValuesFromList(
                data?.data || [],
                (med) => med.medication_name
            ),
        [data?.data]
    );

    const medicationFrequenciesInList = useMemo(
        () => getUniqueValuesFromList(data?.data || [], (med) => med.frequency),
        [data?.data]
    );

    const medicationPrescribersInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (med) =>
                med.prescriber ? displayNameInRightFormat(med.prescriber) : null
            ),
        [data?.data]
    );

    const medicationStatusesInList = useMemo(
        () => getUniqueValuesFromList(data?.data || [], (med) => med.status),
        [data?.data]
    );

    const medicationStartDatesInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (med) =>
                med.start_date ? formatDate(med.start_date, true) : null
            ),
        [data?.data]
    );

    const medicationEndDatesInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (med) =>
                med.end_date ? formatDate(med.end_date, true) : null
            ),
        [data?.data]
    );

    const medicationCreatedDatesInList = useMemo(
        () =>
            getUniqueValuesFromList(data?.data || [], (med) =>
                med.created_at ? formatDate(med.created_at) : null
            ),
        [data?.data]
    );

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else {
            searchParams.set(paramName, value);
        }
        setSearchParams(searchParams);
    };

    const filteredData = useMemo(() => {
        if (!data?.data) return [];

        return data.data.filter((med) => {
            if (nameFilter && med.medication_name !== nameFilter) return false;

            if (frequencyFilter && med.frequency !== frequencyFilter)
                return false;

            if (
                prescriberFilter &&
                (!med.prescriber ||
                    displayNameInRightFormat(med.prescriber) !==
                        prescriberFilter)
            ) {
                return false;
            }

            if (statusFilter && med.status !== statusFilter) return false;

            if (
                startDateFilter &&
                (!med.start_date ||
                    formatDate(med.start_date, true) !== startDateFilter)
            ) {
                return false;
            }

            if (
                endDateFilter &&
                (!med.end_date ||
                    formatDate(med.end_date, true) !== endDateFilter)
            ) {
                return false;
            }

            if (
                createdDateFilter &&
                (!med.created_at ||
                    formatDate(med.created_at) !== createdDateFilter)
            )
                return false;

            return true;
        });
    }, [
        data?.data,
        createdDateFilter,
        endDateFilter,
        frequencyFilter,
        nameFilter,
        prescriberFilter,
        startDateFilter,
        statusFilter,
    ]);

    const handleTriggerSSOLaunch = async () => {
        triggerSSOLaunch.mutate(
            {
                practiceId: practice?.practice_id ?? "",
                payload: {
                    landing_screen: "manage_medications",
                    session_history_id: sessionHistoryId,
                    client_id: clientId ?? "",
                },
            },

            {
                onSuccess: (res) => {
                    setLaunchData(res.data);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ??
                            "Cannot update medications at this time",
                    });
                },
            }
        );
    };

    return (
        <>
            <DrFirstIframe
                launchData={launchData}
                onClose={() => setLaunchData(null)}
                title="Update Medications"
                clientId={clientId ?? ""}
                practiceId={practice?.practice_id ?? ""}
            />
            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-y-[--spacing-items]">
                        <div className="flex items-center justify-between gap-[--spacing-items] flex-wrap">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Name",
                                                nameFilter,
                                                25
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="200px">
                                        {["All", ...medicationNamesInList].map(
                                            (name) => (
                                                <DropdownItem
                                                    key={name}
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            "name",
                                                            name
                                                        )
                                                    }
                                                >
                                                    {truncateString(name, 25)}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Frequency",
                                                frequencyFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {[
                                            "All",
                                            ...medicationFrequenciesInList,
                                        ].map((frequency) => (
                                            <DropdownItem
                                                key={frequency}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "frequency",
                                                        frequency
                                                    )
                                                }
                                            >
                                                {truncateString(frequency, 20)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Prescribers",
                                                prescriberFilter,
                                                25
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="200px">
                                        {[
                                            "All",
                                            ...medicationPrescribersInList,
                                        ].map((prescriber) => (
                                            <DropdownItem
                                                key={prescriber}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "prescriber",
                                                        prescriber
                                                    )
                                                }
                                            >
                                                {truncateString(prescriber, 25)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Status",
                                                statusFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="150px">
                                        {[
                                            "All",
                                            ...medicationStatusesInList,
                                        ].map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "status",
                                                        status
                                                    )
                                                }
                                            >
                                                {truncateString(status, 15)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Start Date",
                                                startDateFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {[
                                            "All",
                                            ...medicationStartDatesInList,
                                        ].map((date) => (
                                            <DropdownItem
                                                key={date}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "startDate",
                                                        date
                                                    )
                                                }
                                            >
                                                {date}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "End Date",
                                                endDateFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {[
                                            "All",
                                            ...medicationEndDatesInList,
                                        ].map((date) => (
                                            <DropdownItem
                                                key={date}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "endDate",
                                                        date
                                                    )
                                                }
                                            >
                                                {date}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getFilterButtonText(
                                                "Date Added",
                                                createdDateFilter
                                            )}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {[
                                            "All",
                                            ...medicationCreatedDatesInList,
                                        ].map((date) => (
                                            <DropdownItem
                                                key={date}
                                                onClick={() =>
                                                    handleFilterChange(
                                                        "createdDate",
                                                        date
                                                    )
                                                }
                                            >
                                                {date}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                            <div>
                                <Button
                                    disabled={triggerSSOLaunch.isLoading}
                                    onClick={handleTriggerSSOLaunch}
                                >
                                    {actionButtonText()}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display client medications`
                        }
                    />
                )}
                {isSuccess && filteredData.length === 0 && (
                    <ListState
                        isError={false}
                        stateHelperText="Medications will appear here when available"
                        emptyMessage="No medications to display"
                    />
                )}
                {isSuccess && filteredData.length > 0 && (
                    <ClientMedicationListTable data={filteredData} />
                )}
            </div>
        </>
    );
}
